import React, { useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Box, Checkbox, FormControlLabel, List, ListItem, ListItemText, Typography } from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'

interface UpdateTemplateRevisionProjectModalProps {
  handleCloseModal?: () => void
  onConfirm: () => void
  isSubmitting?: boolean
}

const IMPACTS = [
  'For Report-per-Instructor survey sections, previously collected responses may not map correctly to the updated survey template.',
  "We'll do our best to migrate previously submitted responses for all live and closed sections to the correct survey template revision.",
  'Reports for already closed survey projects may also be impacted.',
  'Live course sections left with no questions by the new revision will be withdrawn from students, including students who have started the survey.',
  'Course sections whose submitted responses no longer show in reports will get a permanent warning.'
]

export const UpdateTemplateRevisionProjectModal = ({
  handleCloseModal,
  onConfirm,
  isSubmitting = false
}: UpdateTemplateRevisionProjectModalProps): JSX.Element => {
  const [hasAgreed, setHasAgreed] = useState(false)

  return (
    <Box>
      <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography fontWeight='bold' fontSize='0.875rem'>
          The latest published survey template revision will be applied to every course section in this Survey Project, whatever its status, including opted-out course sections.
        </Typography>
      </Alert>

      <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
        Before you proceed, please note:
      </Typography>

      <List dense disablePadding sx={{ mb: 2 }}>
        {IMPACTS.map((impact) => (
          <ListItem key={impact} disableGutters sx={{ alignItems: 'flex-start', gap: 1 }}>
            <Typography component='span' sx={{ mt: '3px', lineHeight: 1, flexShrink: 0 }}>•</Typography>
            <ListItemText primary={impact} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>

      <FormControlLabel
        sx={{ alignItems: 'flex-start', mb: 3, ml: 0 }}
        control={
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => { setHasAgreed(e.target.checked) }}
            sx={{ pt: 0, pl: 0, mr: 1 }}
          />
        }
        label={
          <Typography variant='body2'>
            I understand these impacts and want to apply the latest survey template revision.
          </Typography>
        }
      />

      <DefaultModalActions
        destructiveButtonProps={{ onClick: handleCloseModal }}
        confirmButtonProps={{
          onClick: () => { onConfirm() },
          text: 'Update Revision',
          buttonType: ButtonType.Primary,
          disabled: !hasAgreed,
          loading: isSubmitting
        }}
      />
    </Box>
  )
}
